import {
    useGetContentsByIdsQuery,
    useGetRelatedContentByTagsQuery
} from "@/graphql/generated";
import { useEffect, useMemo } from "react";

interface UseRelatedContentParams {
    contentIds?: string[] | null;
    tags?: string[] | null;
    limit?: number;
}

/**
 * Hook to fetch related content based on vote content_ids and tags
 * Prioritizes content_ids first, then fills remaining slots with tag-based content
 *
 * @param contentIds - Array of specific content IDs to show first (prioritized)
 * @param tags - Array of tags from the vote (fallback)
 * @param limit - Maximum number of related content items (default: 6)
 * @returns Related content data and loading state
 */
export const useRelatedContent = ({
    contentIds,
    tags,
    limit = 6
}: UseRelatedContentParams) => {
    // Fetch content by specific IDs (prioritized)
    const hasContentIds = contentIds && contentIds.length > 0;
    const { data: contentByIds, loading: loadingIds } =
        useGetContentsByIdsQuery({
            variables: { ids: contentIds || [] },
            skip: !hasContentIds
        });

    // Calculate remaining slots for tag-based content
    const prioritizedCount = contentByIds?.mix_content?.length || 0;
    const remainingLimit = Math.max(0, limit - prioritizedCount);

    // Fetch content by tags (fills remaining slots)
    const shouldSkipTags = !tags || tags.length === 0 || remainingLimit === 0;
    const { data: contentByTags, loading: loadingTags } =
        useGetRelatedContentByTagsQuery({
            variables: { tags: tags || [], limit: remainingLimit },
            skip: shouldSkipTags
        });

    const loading = loadingIds || loadingTags;

    // 디버깅: 쿼리 상태 확인
    useEffect(() => {
        console.log("[useRelatedContent] Debug:", {
            contentIds,
            tags,
            hasContentIds,
            shouldSkipTags,
            loading,
            prioritizedCount,
            remainingLimit,
            tagBasedCount: contentByTags?.mix_content?.length || 0,
            totalCount:
                prioritizedCount + (contentByTags?.mix_content?.length || 0)
        });
    }, [
        contentIds,
        tags,
        hasContentIds,
        shouldSkipTags,
        loading,
        prioritizedCount,
        remainingLimit,
        contentByTags
    ]);

    // Merge prioritized content (by IDs) with tag-based content
    const relatedContent = useMemo(() => {
        const prioritized = contentByIds?.mix_content || [];
        const tagBased = contentByTags?.mix_content || [];

        // Filter out duplicates from tag-based content
        const prioritizedIds = new Set(prioritized.map((c) => c.id));
        const uniqueTagBased = tagBased.filter(
            (c) => !prioritizedIds.has(c.id)
        );

        // Combine: prioritized first, then tag-based
        return [...prioritized, ...uniqueTagBased].slice(0, limit);
    }, [contentByIds, contentByTags, limit]);

    return {
        relatedContent,
        loading
    };
};
